<!-- RX_History.vue — graph-sourced -->
<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200">
    <!-- Header -->
    <div class="px-4 py-2.5 border-b border-gray-200">
      <div class="flex items-center justify-between">
        <h3 class="text-base font-semibold text-gray-900">Prescriptions</h3>
        <button
          @click="$emit('refresh')"
          :disabled="loading"
          class="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <FontAwesomeIcon
            :icon="['fas', 'sync']"
            :class="{ 'animate-spin': loading }"
            class="text-sm"
          />
        </button>
      </div>
    </div>

    <!-- AI Summary Banner -->
    <div v-if="summary" class="px-4 py-3 bg-green-50 border-b border-green-100">
      <div class="flex items-start gap-2">
        <FontAwesomeIcon :icon="['fas', 'wand-magic-sparkles']" class="text-[10px] text-green-500 mt-1 flex-shrink-0" />
        <p class="text-xs text-green-800 leading-relaxed">{{ summary }}</p>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <FontAwesomeIcon :icon="['fas', 'spinner']" class="text-xl text-gray-400 animate-spin" />
    </div>

    <!-- Empty -->
    <div v-else-if="allMeds.length === 0 && surescriptsFills.length === 0" class="text-center py-8 px-4">
      <FontAwesomeIcon :icon="['fas', 'pills']" class="text-2xl text-gray-300 mb-2" />
      <p class="text-sm text-gray-500">No medication data found</p>
    </div>

    <div v-else class="p-4 space-y-4">
      <!-- Source Tabs -->
      <div class="flex gap-1.5">
        <button
          v-for="src in sources"
          :key="src.id"
          @click="activeSource = src.id"
          :class="[
            'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
            activeSource === src.id
              ? 'bg-gray-900 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
          ]"
        >
          {{ src.label }} ({{ src.count }})
        </button>
      </div>

      <!-- Pharmacy Claims (Part D NDCs) -->
      <div v-if="activeSource === 'pharmacy'" class="space-y-2">
        <div
          v-for="(med, i) in pharmacyMeds"
          :key="i"
          class="border border-gray-200 rounded px-2.5 py-1.5"
        >
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0 flex-1">
              <div class="text-xs font-medium text-gray-900">{{ med.name }}</div>
              <div v-if="med.latest_fill" class="text-[10px] text-gray-500 mt-0.5">
                Last fill: {{ formatDate(med.latest_fill) }}
                <span v-if="med.fill_count > 1" class="ml-1">({{ med.fill_count }} fills)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Surescripts pharmacy fills -->
      <div v-if="activeSource === 'surescripts'" class="space-y-3">
        <p class="text-[10px] text-gray-500 leading-snug">
          Dispensed at the pharmacy, reported by Surescripts. Confirms the pharmacy
          filled the prescription, not that the patient collected it. Typically
          3–5 days behind the fill.
        </p>
        <div v-for="(grp, i) in surescriptsByDrug" :key="i" class="border border-gray-200 rounded">
          <div class="px-2.5 py-1.5 bg-gray-50 border-b border-gray-200">
            <div class="flex items-start justify-between gap-2">
              <div class="min-w-0 flex-1">
                <div class="text-xs font-medium text-gray-900">
                  {{ grp.drug_name || 'Unknown medication' }}
                  <span
                    v-if="grp.dea_schedule"
                    class="ml-1 px-1 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-medium"
                  >C-{{ grp.dea_schedule }}</span>
                </div>
                <div class="text-[10px] text-gray-500 mt-0.5">
                  {{ grp.fills.length }} fill{{ grp.fills.length === 1 ? '' : 's' }}
                  · last {{ formatDate(grp.latest.filled_dt) }}
                  <span v-if="grp.latest.pharmacy_name"> · {{ grp.latest.pharmacy_name }}</span>
                </div>
              </div>
              <div v-if="grp.latest.days_supply" class="text-[10px] text-gray-500 whitespace-nowrap">
                {{ grp.latest.days_supply }}d supply
              </div>
            </div>
          </div>
          <div
            v-for="(f, j) in grp.fills"
            :key="j"
            class="px-2.5 py-1 flex items-center justify-between gap-2 border-t border-gray-100 first:border-t-0"
          >
            <span class="text-[11px] text-gray-900">{{ formatDate(f.filled_dt) }}</span>
            <span class="text-[10px] text-gray-500 truncate">
              <span v-if="f.days_supply">{{ f.days_supply }}d</span>
              <span v-if="f.quantity"> · qty {{ f.quantity }}</span>
              <span v-if="f.prescriber_name"> · {{ f.prescriber_name }}</span>
            </span>
          </div>
        </div>
      </div>

      <!-- EHR Medications -->
      <div v-if="activeSource === 'ehr'" class="space-y-2">
        <div
          v-for="(med, i) in ehrMeds"
          :key="i"
          class="border border-gray-200 rounded px-2.5 py-1.5"
        >
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0 flex-1">
              <div class="text-xs font-medium text-gray-900">{{ med.MED_DETAILS || 'Unknown' }}</div>
              <div class="text-[10px] text-gray-500 mt-0.5 space-x-2">
                <span v-if="med.STATUS">{{ med.STATUS }}</span>
                <span v-if="med.START_DATE">Since {{ formatDate(med.START_DATE) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'RX_History',
  components: { FontAwesomeIcon },
  props: {
    medications: { type: Array, default: () => [] },
    surescriptsFills: { type: Array, default: () => [] },
    claims: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    summary: { type: String, default: '' }
  },
  emits: ['refresh'],
  data() {
    return {
      activeSource: 'pharmacy',
    };
  },
  computed: {
    pharmacyMeds() {
      // Deduplicate NDC descriptions from Part D claims
      const map = {};
      for (const c of this.claims) {
        if (c.CLM_TYPE_CD !== 'D' && c.CLM_TYPE_CD !== '70') continue;
        for (const name of (c.medications || [])) {
          if (!map[name]) {
            map[name] = { name, fills: [], fill_count: 0 };
          }
          if (c.CLM_FROM_DT) map[name].fills.push(c.CLM_FROM_DT);
          map[name].fill_count++;
        }
      }
      return Object.values(map).map(m => ({
        name: m.name,
        fill_count: m.fill_count,
        latest_fill: m.fills.sort().pop() || '',
      })).sort((a, b) => (b.latest_fill || '').localeCompare(a.latest_fill || ''));
    },
    ehrMeds() {
      return [...this.medications].sort((a, b) =>
        (b.START_DATE || '').localeCompare(a.START_DATE || '')
      );
    },
    allMeds() {
      return [...this.pharmacyMeds, ...this.ehrMeds];
    },
    sources() {
      return [
        { id: 'pharmacy', label: 'Pharmacy Claims', count: this.pharmacyMeds.length },
        { id: 'ehr', label: 'EHR Medications', count: this.ehrMeds.length },
        { id: 'surescripts', label: 'Pharmacy Fills', count: this.surescriptsFills.length },
      ];
    },

    // Group the flat fill list by drug, newest first, so a patient on one drug
    // for a year reads as one entry with its history rather than 12 rows.
    sourceSignature() {
      return `${this.pharmacyMeds.length}|${this.ehrMeds.length}|${this.surescriptsFills.length}`;
    },

    surescriptsByDrug() {
      const map = new Map();
      for (const f of this.surescriptsFills) {
        const key = f.drug_name || 'Unknown medication';
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(f);
      }
      return [...map.entries()]
        .map(([drug_name, fills]) => ({
          drug_name,
          fills,
          latest: fills[0],
          dea_schedule: fills.find(x => x.dea_schedule)?.dea_schedule || null,
        }))
        .sort((a, b) => (b.latest.filled_dt || '').localeCompare(a.latest.filled_dt || ''));
    }
  },
  watch: {
    // Keyed on both sources: surescripts arrives on its own request, so a watcher
    // on allMeds alone would leave the tab defaulted before that data lands.
    sourceSignature: {
      immediate: true,
      handler() {
        // Default to whichever source has data
        if (this.surescriptsFills.length) this.activeSource = 'surescripts';
        else if (this.pharmacyMeds.length) this.activeSource = 'pharmacy';
        else if (this.ehrMeds.length) this.activeSource = 'ehr';
      }
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });
    }
  }
};
</script>
